import {
  MERGE_REQUIRED,
  SPLINTER_MIN_TIER,
  SPLINTER_FRAGMENT_COUNT,
  Tier,
  TIER_POINTS,
  Family,
} from '../config';
import {
  ElementInstance,
  getElementDefOrThrow,
  getMergeResult,
  getSplinterResult,
  elementIdFor,
} from '../data/elements';
import { Grid } from './Grid';
import { UidAllocator } from './UidAllocator';

/**
 * Result of a successful merge operation.
 */
export interface MergeResult {
  resultElement: ElementInstance;
  pointsEarned: number;
  newDiscovery: boolean;
}

/**
 * Result of a successful splinter operation.
 */
export interface SplinterResult {
  sourceElement: ElementInstance;
  fragments: ElementInstance[];
}

/**
 * MergeEngine handles the core merge and splinter logic for the game.
 */
export class MergeEngine {
  private grid: Grid;
  private isDiscovered: (defId: string) => boolean;
  private uids: UidAllocator;

  constructor(
    grid: Grid,
    isDiscovered?: (defId: string) => boolean,
    uids?: UidAllocator
  ) {
    this.grid = grid;
    this.isDiscovered = isDiscovered ?? ((defId) => getElementDefOrThrow(defId).discovered);
    this.uids = uids ?? new UidAllocator(1);
  }

  /**
   * Check if two elements can be merged.
   * Standard: same defId, tier < Eternal.
   * Wild: one Wild + one non-Wild at same tier, tier < Eternal.
   */
  canMerge(source: ElementInstance, target: ElementInstance): boolean {
    // Corrupted elements cannot merge
    if (source.corrupted || target.corrupted) return false;

    const sourceDef = getElementDefOrThrow(source.defId);
    const targetDef = getElementDefOrThrow(target.defId);

    // Wild merge: one wild + one non-wild at same tier
    const sourceIsWild = sourceDef.family === Family.Wild;
    const targetIsWild = targetDef.family === Family.Wild;

    if (sourceIsWild || targetIsWild) {
      // Wild + Wild cannot merge
      if (sourceIsWild && targetIsWild) return false;
      // Must be same tier and below Eternal
      if (sourceDef.tier !== targetDef.tier) return false;
      if (sourceDef.tier >= Tier.Eternal) return false;
      // The non-wild element must have a merge result
      const nonWildDef = sourceIsWild ? targetDef : sourceDef;
      return getMergeResult(nonWildDef.id) !== undefined;
    }

    // Standard merge: same defId
    if (source.defId !== target.defId) return false;
    if (sourceDef.tier >= Tier.Eternal) return false;
    return getMergeResult(source.defId) !== undefined;
  }

  /**
   * Perform a merge operation.
   * Removes both source and target, places the merged result at target position.
   * Returns the merge result or null if merge cannot be performed.
   */
  merge(
    sourceCol: number,
    sourceRow: number,
    targetCol: number,
    targetRow: number
  ): MergeResult | null {
    const source = this.grid.get(sourceCol, sourceRow);
    const target = this.grid.get(targetCol, targetRow);

    if (!source || !target) {
      return null;
    }

    if (!this.canMerge(source, target)) {
      return null;
    }

    // Determine merge result: for wild merges, use the non-wild element's family
    const sourceDef = getElementDefOrThrow(source.defId);
    const targetDef = getElementDefOrThrow(target.defId);
    let mergeResultId: string | undefined;

    if (sourceDef.family === Family.Wild || targetDef.family === Family.Wild) {
      const nonWildDef = sourceDef.family === Family.Wild ? targetDef : sourceDef;
      mergeResultId = getMergeResult(nonWildDef.id);
    } else {
      mergeResultId = getMergeResult(source.defId);
    }

    if (!mergeResultId) {
      return null;
    }

    // Remove both elements
    this.grid.remove(sourceCol, sourceRow);
    this.grid.remove(targetCol, targetRow);

    // Create the merged element at target position
    const resultDef = getElementDefOrThrow(mergeResultId);
    const newDiscovery = !this.isDiscovered(mergeResultId);

    const resultElement: ElementInstance = {
      uid: this.uids.mint(),
      defId: mergeResultId,
      col: targetCol,
      row: targetRow,
      age: 0,
      corrupted: false,
    };

    this.grid.set(targetCol, targetRow, resultElement);

    const pointsEarned = TIER_POINTS[resultDef.tier];

    return {
      resultElement,
      pointsEarned,
      newDiscovery,
    };
  }

  /**
   * Check if an element can be splintered (tier >= SPLINTER_MIN_TIER).
   */
  canSplinter(element: ElementInstance): boolean {
    const def = getElementDefOrThrow(element.defId);
    return def.tier >= SPLINTER_MIN_TIER;
  }

  /**
   * Perform a splinter operation.
   * Removes the element, spawns SPLINTER_FRAGMENT_COUNT fragments (tier-1) in random empty cells.
   * Returns the splinter result or null if splinter cannot be performed.
   */
  splinter(col: number, row: number): SplinterResult | null {
    const element = this.grid.get(col, row);
    if (!element) {
      return null;
    }

    if (!this.canSplinter(element)) {
      return null;
    }

    const def = getElementDefOrThrow(element.defId);
    const splinterResultId = getSplinterResult(element.defId);

    if (!splinterResultId) {
      return null;
    }

    // Remove the source element
    this.grid.remove(col, row);

    // Create fragments
    const fragments: ElementInstance[] = [];
    for (let i = 0; i < SPLINTER_FRAGMENT_COUNT; i++) {
      const emptyCell = this.grid.findEmpty();
      if (!emptyCell) {
        // No more space, stop spawning
        break;
      }

      const fragment: ElementInstance = {
        uid: this.uids.mint(),
        defId: splinterResultId,
        col: emptyCell.col,
        row: emptyCell.row,
        age: 0,
        corrupted: false,
      };

      this.grid.set(emptyCell.col, emptyCell.row, fragment);
      fragments.push(fragment);
    }

    return {
      sourceElement: element,
      fragments,
    };
  }

}
